import React from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { StackScreenProps } from '@react-navigation/stack';
import { colors, fontSizes, fontWeights, radius, spacing } from '../theme';
import { formatDateLong, getServiceById } from '../data/shopData';
import type { RootStackParamList } from '../navigation/AppNavigator';

type Props = StackScreenProps<RootStackParamList, 'Confirmation'>;

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.summaryRow}>
      <Text style={styles.summaryLabel}>{label}</Text>
      <Text style={styles.summaryValue}>{value}</Text>
    </View>
  );
}

export function ConfirmationScreen({ navigation, route }: Props) {
  const { confirmationCode, serviceId, date, time, customerName, vehicle } = route.params;

  const service = getServiceById(serviceId);
  const formattedDate = date ? formatDateLong(date) : '';

  function handleReturnHome() {
    navigation.popToTop();
  }

  function handleBookAnother() {
    navigation.navigate('Booking', {});
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        {/* ── Check icon ──────────────────────────────────────────── */}
        <View style={styles.iconWrapper}>
          <View style={styles.iconCircle}>
            <Text style={styles.iconText}>✓</Text>
          </View>
        </View>

        {/* ── Heading ─────────────────────────────────────────────── */}
        <Text style={styles.heading}>Appointment Booked!</Text>
        <Text style={styles.subheading}>
          We'll send a confirmation to your email shortly. Our team will reach out to confirm your appointment.
        </Text>

        {/* ── Confirmation code ────────────────────────────────────── */}
        <View style={styles.codeCard}>
          <Text style={styles.codeLabel}>Confirmation Code</Text>
          <Text style={styles.codeValue}>{confirmationCode}</Text>
          <Text style={styles.codeHint}>Save this code for your records</Text>
        </View>

        {/* ── Summary ──────────────────────────────────────────────── */}
        <View style={styles.summaryCard}>
          <Text style={styles.summaryTitle}>Appointment Details</Text>
          {service && <SummaryRow label="Service" value={service.name} />}
          <SummaryRow label="Name" value={customerName} />
          {vehicle.trim() !== '' && <SummaryRow label="Vehicle" value={vehicle} />}
          {formattedDate !== '' && <SummaryRow label="Date" value={formattedDate} />}
          {time !== '' && <SummaryRow label="Time" value={time} />}
        </View>

        {/* ── Info box ─────────────────────────────────────────────── */}
        <View style={styles.infoBox}>
          <Text style={styles.infoText}>
            📍  1428 Industrial Blvd, Springfield, IL 62701{'\n'}
            📞  (555) 482-7700{'\n'}
            🕐  Mon–Fri 8AM–5PM · Sat 9AM–2PM
          </Text>
        </View>

        {/* ── Actions ──────────────────────────────────────────────── */}
        <View style={styles.actions}>
          <TouchableOpacity
            style={styles.primaryBtn}
            onPress={handleReturnHome}
            activeOpacity={0.85}
          >
            <Text style={styles.primaryBtnText}>Return to Home</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.secondaryBtn}
            onPress={handleBookAnother}
            activeOpacity={0.75}
          >
            <Text style={styles.secondaryBtnText}>Book Another Appointment</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    maxWidth: 520,
    alignSelf: 'center',
    width: '100%',
    padding: spacing.lg,
    paddingTop: spacing.xxl,
    alignItems: 'center',
  },

  // Check icon
  iconWrapper: {
    marginBottom: spacing.lg,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: radius.pill,
    backgroundColor: colors.successLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconText: {
    fontSize: 36,
    color: colors.success,
    fontWeight: fontWeights.bold,
  },

  // Heading
  heading: {
    fontSize: fontSizes.xxl,
    fontWeight: fontWeights.extrabold,
    color: colors.textPrimary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  subheading: {
    fontSize: fontSizes.base,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 23,
    marginBottom: spacing.xl,
    paddingHorizontal: spacing.md,
  },

  // Confirmation code
  codeCard: {
    backgroundColor: colors.primary,
    borderRadius: radius.xl,
    padding: spacing.lg,
    alignItems: 'center',
    width: '100%',
    marginBottom: spacing.md,
  },
  codeLabel: {
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.semibold,
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: spacing.sm,
  },
  codeValue: {
    fontSize: fontSizes.xxl,
    fontWeight: fontWeights.extrabold,
    color: colors.white,
    letterSpacing: 4,
    marginBottom: spacing.xs,
  },
  codeHint: {
    fontSize: fontSizes.xs,
    color: '#64748B',
  },

  // Summary
  summaryCard: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: spacing.lg,
    width: '100%',
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  summaryTitle: {
    fontSize: fontSizes.base,
    fontWeight: fontWeights.semibold,
    color: colors.textPrimary,
    marginBottom: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  summaryLabel: {
    fontSize: fontSizes.sm,
    color: colors.textSecondary,
    flex: 1,
  },
  summaryValue: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.medium,
    color: colors.textPrimary,
    flex: 2,
    textAlign: 'right',
  },

  // Info box
  infoBox: {
    backgroundColor: colors.surfaceAlt,
    borderRadius: radius.lg,
    padding: spacing.md,
    width: '100%',
    marginBottom: spacing.xl,
    borderWidth: 1,
    borderColor: colors.border,
  },
  infoText: {
    fontSize: fontSizes.sm,
    color: colors.textSecondary,
    lineHeight: 24,
  },

  // Actions
  actions: {
    width: '100%',
    gap: spacing.sm,
  },
  primaryBtn: {
    backgroundColor: colors.accent,
    paddingVertical: spacing.md,
    borderRadius: radius.pill,
    alignItems: 'center',
  },
  primaryBtnText: {
    color: colors.white,
    fontSize: fontSizes.base,
    fontWeight: fontWeights.semibold,
  },
  secondaryBtn: {
    paddingVertical: spacing.md,
    borderRadius: radius.pill,
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  secondaryBtnText: {
    color: colors.textPrimary,
    fontSize: fontSizes.base,
    fontWeight: fontWeights.medium,
  },
});
